import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import {
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Select,
  SelectItem,
  Tooltip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';

import useUserStore from '../../../../../common/src/stores/useUserStore';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { Settlement } from 'common/src/utils/types';
import {
  getSinisterSettlements,
  getSinisterClaimsBySettlementIds,
  SinisterClaimPresence,
} from 'common/src/api/siniestros';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import SinisterEditPopover from '../../../components/settlements/sinister-edit-popover';
import SettlementHistoryDrawer from '../../../components/settlements/settlement-history-drawer';
import Header from '../../../components/layouts/Header';
import { LoadingState, PageLayout } from '../../../components/ui';
import { getSettlementHistoryCounts } from '../../../services/historyService';

const SHIP_TYPE_KEY: Record<string, string> = {
  M: 'Marítimo',
  A: 'Aéreo',
  T: 'Terrestre',
};

type SortColumn =
  | 'reference_number'
  | 'container_number'
  | 'receiver'
  | 'ship_name'
  | 'ship_type'
  | 'date_sold'
  | 'market'
  | 'responsible'
  | 'delay_days'
  | 'case_number'
  | 'kilos';

interface SortDesc {
  column: SortColumn;
  direction: 'ascending' | 'descending';
}

export default function SiniestrosPage() {
  const { t } = useTranslation('common');
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const [, setLocation] = useLocation();

  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [claimsBySettlement, setClaimsBySettlement] = useState<
    Map<number, SinisterClaimPresence>
  >(new Map());
  const [historyCounts, setHistoryCounts] = useState<Map<number, number>>(
    new Map()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [responsibleFilter, setResponsibleFilter] = useState<string>('');
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedHistorySettlement, setSelectedHistorySettlement] =
    useState<Settlement | null>(null);
  const [sortDescriptor, setSortDescriptor] = useState<SortDesc>({
    column: 'date_sold',
    direction: 'descending',
  });

  useEffect(() => {
    const load = async () => {
      if (!userProfile?.exporter_id || !selectedSpecies?.id) return;
      setIsLoading(true);
      try {
        const data = await getSinisterSettlements(
          +userProfile.exporter_id,
          selectedSpecies.id,
          selectedSeason,
          responsibleFilter || undefined
        );
        setSettlements(data);
        const settlementIds = data.map((s) => s.id);
        if (data.length > 0) {
          const [claimsMap, countsMap] = await Promise.all([
            getSinisterClaimsBySettlementIds(settlementIds),
            getSettlementHistoryCounts(settlementIds),
          ]);
          setClaimsBySettlement(claimsMap);
          setHistoryCounts(countsMap);
        } else {
          setClaimsBySettlement(new Map());
          setHistoryCounts(new Map());
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [
    userProfile?.exporter_id,
    selectedSpecies?.id,
    selectedSeason,
    responsibleFilter,
  ]);

  const totalKilosOf = (s: Settlement) =>
    s.settlement_details?.reduce((acc, d) => acc + (Number(d.kilos) || 0), 0) ||
    0;

  const sortedRows = useMemo(() => {
    const arr = [...settlements];
    const dir = sortDescriptor.direction === 'ascending' ? 1 : -1;
    const get = (s: Settlement): string | number => {
      switch (sortDescriptor.column) {
        case 'reference_number':
          return s.reference_number || '';
        case 'container_number':
          return s.container_number || '';
        case 'receiver':
          return s.receiver || '';
        case 'ship_name':
          return s.ship_trip?.ship?.name || '';
        case 'ship_type':
          return s.ship_trip?.ship?.type || '';
        case 'date_sold':
          return s.date_sold || '';
        case 'market':
          return s.market?.name || String(s.market_id || '');
        case 'responsible':
          return s.sinister_responsible || '';
        case 'delay_days':
          return Number(s.sinister_delay_days || 0);
        case 'case_number':
          return s.sinister_case_number || '';
        case 'kilos':
          return totalKilosOf(s);
        default:
          return '';
      }
    };
    arr.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [settlements, sortDescriptor]);

  const totals = useMemo(() => {
    const totalKilos = settlements.reduce((acc, s) => acc + totalKilosOf(s), 0);
    return { count: settlements.length, totalKilos };
  }, [settlements]);

  const goToDetail = (id: number) =>
    setLocation(`/settlements/siniestros/${id}`);

  const reloadHistoryCounts = async () => {
    const settlementIds = settlements.map((s) => s.id);
    if (settlementIds.length === 0) {
      setHistoryCounts(new Map());
      return;
    }
    const countsMap = await getSettlementHistoryCounts(settlementIds);
    setHistoryCounts(countsMap);
  };

  return (
    <PageLayout>
      <Header
        title={t('Siniestros')}
        description={t(
          'Listado de contenedores siniestrados. Click en un contenedor para ver y valorizar su certificado de siniestro.'
        )}
      />

      <Card className='mb-4'>
        <CardBody className='flex flex-col md:flex-row gap-3'>
          <SeasonFilter />
          <Select
            label={t('Responsable')}
            placeholder={t('Todos')}
            selectedKeys={responsibleFilter ? [responsibleFilter] : []}
            onChange={(e) => setResponsibleFilter(e.target.value)}
            className='max-w-xs'
          >
            {[
              <SelectItem key='' value=''>
                {t('Todos')}
              </SelectItem>,
              ...SINISTER_RESPONSIBLE_OPTIONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(r)}
                </SelectItem>
              )),
            ]}
          </Select>
          <div className='flex items-center gap-4 md:ml-auto text-sm'>
            <div>
              <span className='text-default-500'>{t('Contenedores')}: </span>
              <span className='font-semibold'>{totals.count}</span>
            </div>
            <div>
              <span className='text-default-500'>{t('Kilos')}: </span>
              <span className='font-semibold'>
                {totals.totalKilos.toLocaleString()}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {isLoading ? (
        <LoadingState />
      ) : (
        <Table
          aria-label={t('Siniestros')}
          sortDescriptor={sortDescriptor as any}
          onSortChange={(d) => setSortDescriptor(d as SortDesc)}
        >
          <TableHeader>
            <TableColumn key='actions'> </TableColumn>
            <TableColumn key='case_number' allowsSorting>
              {t('Número de Caso')}
            </TableColumn>
            <TableColumn key='reference_number' allowsSorting>
              {t('Referencia')}
            </TableColumn>
            <TableColumn key='container_number' allowsSorting>
              {t('Contenedor')}
            </TableColumn>
            <TableColumn key='receiver' allowsSorting>
              {t('Cliente')}
            </TableColumn>
            <TableColumn key='ship_name' allowsSorting>
              {t('Nave')}
            </TableColumn>
            <TableColumn key='ship_type' allowsSorting>
              {t('Tipo')}
            </TableColumn>
            <TableColumn key='date_sold' allowsSorting>
              {t('Fecha de Venta')}
            </TableColumn>
            <TableColumn key='market' allowsSorting>
              {t('Mercado')}
            </TableColumn>
            <TableColumn key='responsible' allowsSorting>
              {t('Responsable')}
            </TableColumn>
            <TableColumn key='delay_days' allowsSorting>
              <Tooltip
                content={t(
                  'Días que el siniestro postergó la venta. Se mide desde la FECHA DE VENTA original (no desde la ETA de la nave).'
                )}
                placement='top'
              >
                <span className='inline-flex items-center gap-1'>
                  {t('Días Retraso')}
                  <Icon
                    icon='tabler:info-circle'
                    className='text-default-400'
                  />
                </span>
              </Tooltip>
            </TableColumn>

            <TableColumn key='kilos' allowsSorting>
              {t('Kilos')}
            </TableColumn>
            <TableColumn key='claim_market'>
              <Tooltip
                content={t('Claim guardado para vs Mercado')}
                placement='top'
              >
                <span className='inline-flex items-center gap-1'>
                  <Icon
                    icon='tabler:world'
                    className='text-blue-600 dark:text-blue-400'
                  />
                  {t('Mercado')}
                </span>
              </Tooltip>
            </TableColumn>
            <TableColumn key='claim_exporter'>
              <Tooltip
                content={t('Claim guardado para vs Exportadora')}
                placement='top'
              >
                <span className='inline-flex items-center gap-1'>
                  <Icon
                    icon='tabler:building-factory-2'
                    className='text-emerald-600 dark:text-emerald-400'
                  />
                  {t('Exportadora')}
                </span>
              </Tooltip>
            </TableColumn>
            <TableColumn key='claim_industry'>
              <Tooltip
                content={t('Claim guardado para vs Industria')}
                placement='top'
              >
                <span className='inline-flex items-center gap-1'>
                  <Icon
                    icon='tabler:chart-histogram'
                    className='text-violet-600 dark:text-violet-400'
                  />
                  {t('Industria')}
                </span>
              </Tooltip>
            </TableColumn>
          </TableHeader>
          <TableBody emptyContent={t('No hay contenedores siniestrados')}>
            {sortedRows.map((s) => {
              const kilos = totalKilosOf(s);
              const shipType = s.ship_trip?.ship?.type || '-';
              return (
                <TableRow
                  key={s.id}
                  className='cursor-pointer hover:bg-default-100'
                  onClick={() => goToDetail(s.id)}
                >
                  <TableCell>
                    <div
                      className='flex items-center gap-1'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <BitacoraButton
                        count={historyCounts.get(s.id) || 0}
                        label={t('Ver Bitácora')}
                        onPress={() => {
                          setSelectedHistorySettlement(s);
                          setIsHistoryOpen(true);
                        }}
                      />
                      <SinisterEditPopover
                        settlement={s}
                        onSave={(responsible, delayDays, caseNumber) => {
                          setSettlements((prev) =>
                            prev.map((row) =>
                              row.id === s.id
                                ? {
                                    ...row,
                                    sinister_responsible: responsible,
                                    sinister_delay_days: delayDays,
                                    sinister_case_number: caseNumber,
                                  }
                                : row
                            )
                          );
                        }}
                        trigger={
                          <Button
                            isIconOnly
                            size='sm'
                            variant='light'
                            aria-label={t('Editar siniestro')}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icon icon='tabler:edit' className='text-default-500' />
                          </Button>
                        }
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    {s.sinister_case_number ? (
                      <Chip size='sm' variant='flat'>
                        {s.sinister_case_number}
                      </Chip>
                    ) : (
                      <span className='text-default-300'>—</span>
                    )}
                  </TableCell>
                  <TableCell>{s.reference_number || '-'}</TableCell>
                  <TableCell>{s.container_number || '-'}</TableCell>
                  <TableCell>{s.receiver}</TableCell>
                  <TableCell>{s.ship_trip?.ship?.name || '-'}</TableCell>
                  <TableCell>
                    {SHIP_TYPE_KEY[shipType]
                      ? t(SHIP_TYPE_KEY[shipType])
                      : shipType}
                  </TableCell>
                  <TableCell>
                    {s.date_sold
                      ? new Date(s.date_sold).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>{s.market?.name || s.market_id || '-'}</TableCell>
                  <TableCell>
                    {s.sinister_responsible ? (
                      <Chip size='sm' variant='flat' color='warning'>
                        {t(s.sinister_responsible)}
                      </Chip>
                    ) : (
                      <Chip size='sm' variant='flat'>
                        {t('Sin asignar')}
                      </Chip>
                    )}
                  </TableCell>
                  <TableCell>
                    {s.sinister_delay_days && s.sinister_delay_days > 0 ? (
                      <Chip size='sm' variant='flat' color='danger'>
                        {t('{{count}} d', { count: s.sinister_delay_days })}
                      </Chip>
                    ) : (
                      <Chip size='sm' variant='flat'>
                        {t('Sin retraso')}
                      </Chip>
                    )}
                  </TableCell>

                  <TableCell>{kilos.toLocaleString()}</TableCell>
                  <TableCell>
                    <ClaimMark claim={claimsBySettlement.get(s.id)?.market} />
                  </TableCell>
                  <TableCell>
                    <ClaimMark claim={claimsBySettlement.get(s.id)?.exporter} />
                  </TableCell>
                  <TableCell>
                    <ClaimMark claim={claimsBySettlement.get(s.id)?.industry} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <SettlementHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={() => {
          setIsHistoryOpen(false);
          setSelectedHistorySettlement(null);
        }}
        settlement={selectedHistorySettlement}
        onChange={reloadHistoryCounts}
      />
    </PageLayout>
  );
}

function BitacoraButton({
  count,
  label,
  onPress,
}: {
  count: number;
  label: string;
  onPress: () => void;
}) {
  return (
    <Tooltip content={label}>
      <div className='relative'>
        <Button
          isIconOnly
          size='sm'
          variant='light'
          aria-label={label}
          onPress={onPress}
        >
          <Icon icon='tabler:book' className='text-default-500' />
        </Button>
        {count > 0 && (
          <span className='absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white shadow-sm'>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

// Indicator for the "claim saved?" columns. Shows a check chip with status
// when an active claim exists for that subreport, or a dash when not.
function ClaimMark({
  claim,
}: {
  claim?: { id: number; status: string } | null;
}) {
  if (!claim) {
    return <span className='text-default-300'>—</span>;
  }
  const statusColor: Record<
    string,
    'default' | 'primary' | 'success' | 'warning' | 'danger'
  > = {
    draft: 'default',
    submitted: 'primary',
    accepted: 'success',
    distributed: 'success',
    cancelled: 'danger',
    denied: 'danger',
  };
  return (
    <Tooltip content={`Claim #${claim.id} · ${claim.status}`} placement='top'>
      <Chip
        size='sm'
        variant='flat'
        color={statusColor[claim.status] || 'default'}
        startContent={<Icon icon='tabler:check' className='ml-1 text-xs' />}
      >
        {claim.status}
      </Chip>
    </Tooltip>
  );
}
